import { useEffect, useMemo } from 'react';

import { TrustSignals } from '@dbt-labs/metadata-shared';
import { RyeconClock, RyeconTable } from '@dbt-labs/sourdough';

import { SourceHealthIssueType } from '@/__generated__/graphql';
import { ColumnTable } from '@/components/ColumnTable';
import { InheritedDescriptionDisplay } from '@/components/Description';
import { NO_DESCRIPTION } from '@/components/DescriptionForm';
import { Page } from '@/components/Page';
import { DetailsSection } from '@/components/SectionWithCard';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { NodeStatusCell } from '@/components/Table/NodeStatusCell';
import { SourceTrustSignalCell } from '@/components/Table/SourceTrustSignalCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { DescriptionChangesProvider } from '@/context/DescriptionChangesProvider';
import { useMissingDataHeader } from '@/context/MissingDataHeaderProvider';
import { ResourceInfoProvider } from '@/context/ResourceInfoProvider';
import { useDiscoveryAppliedSourceCollection } from '@/hooks/discovery/appliedSourceCollection';
import { useDiscoveryAppliedSources } from '@/hooks/discovery/appliedSources';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams, useValidatedParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { formatAbsoluteLocalDate, formatDateCommon } from '@/util/date';
import { computeSourceFreshness } from '@/util/dbt/freshness';
import { getPatchPath } from '@/util/dbt/patchPath';
import {
  INITIAL_PAGE_SIZE,
  ResourceFilterTable,
} from '../../../components/ResourceFilterTable';
import { ResourceDetailsHeader, TextIconPair } from '../Header';

type SourceTableData = {
  name: string | null;
  status: string | null;
  snapshottedAt: string | null;
  maxLoadedAt: string | null;
  identifier: string | null;
  uniqueId: string | null;
  trustSignals: TrustSignals;
};

type SourceAppliedListNode = {
  name: string | null;
  freshness: {
    freshnessChecked: boolean | null;
    freshnessStatus: string | null;
    maxLoadedAt: string | null;
    snapshottedAt: string | null;
  } | null;
  identifier: string | null;
  uniqueId: string;
  healthIssues: SourceHealthIssueType[];
};

const getTableData = (source: SourceAppliedListNode): SourceTableData => {
  const status = computeSourceFreshness(source?.freshness);

  return {
    name: parseUniqueId(source.uniqueId).remainder,
    status,
    snapshottedAt: source.freshness?.snapshottedAt ?? null,
    maxLoadedAt: source.freshness?.maxLoadedAt ?? null,
    identifier: source.identifier,
    uniqueId: source.uniqueId,
    trustSignals: {
      healthIssues: source.healthIssues,
      additionalMetadata: {
        freshnessChecked: source.freshness?.freshnessChecked,
      },
    },
  };
};

export const SourceCollectionPage = () => {
  const { sourceName } = useValidatedParams();
  const { accountId, environmentId } = useDbtCloudProject();
  const projectScope = useUrlProjectParams();
  const { userId } = useEnvironmentScope();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const [sourceCollectionQuery] = useDiscoveryAppliedSourceCollection({
    environmentId,
    userId,
    sourceName,
  });
  const pageSize = INITIAL_PAGE_SIZE;
  const paginatedSourcesQuery = useDiscoveryAppliedSources({
    environmentId,
    userId,
    filter: { sourceNames: sourceName ? [sourceName] : undefined },
    first: pageSize,
  });
  const { setMissingSourceFreshness } = useMissingDataHeader();

  const sources = paginatedSourcesQuery.data;
  const pageInfo = paginatedSourcesQuery.pageInfo;

  const data = useMemo((): any[] | undefined => {
    return sources?.map((source) => getTableData(source));
  }, [sources]);

  const isLoadingMore = paginatedSourcesQuery.fetching;
  const isInitialLoad =
    paginatedSourcesQuery.fetching && paginatedSourcesQuery.data === undefined;

  const sourceCollection = sourceCollectionQuery.fetching
    ? null
    : sourceCollectionQuery.data?.environment.applied;
  const firstSource = sourceCollection?.sources?.edges?.[0]?.node;
  const description = firstSource?.sourceDescription;
  const date = sourceCollection?.lastUpdatedAt
    ? new Date(sourceCollection.lastUpdatedAt)
    : undefined;
  const lastUpdatedAt = date && formatAbsoluteLocalDate(date);
  const lastUpdatedAtUtc = date && formatDateCommon(date).utc;
  const totalTables = sourceCollection?.sources?.totalCount;
  const headerIcons: TextIconPair[] = [];

  if (lastUpdatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Updated at ${lastUpdatedAt}`,
      tooltip: lastUpdatedAtUtc || undefined,
    });
  }
  if (totalTables) {
    headerIcons.push({
      ryecon: RyeconTable,
      text: `${totalTables} tables`,
    });
  }

  const sourceCollectionDetailTableContents = [
    {
      key: 'loader',
      data: firstSource?.loader,
    },
    {
      key: 'database',
      data: firstSource?.database,
    },
    {
      key: 'schema',
      data: firstSource?.schema,
    },
    {
      key: 'meta',
      data: JSON.stringify(firstSource?.meta),
    },
  ];

  useEffect(() => {
    const isMissingSourceFreshness =
      !!sources &&
      !!sources?.every((source) => source.freshness?.freshnessStatus == null);
    setMissingSourceFreshness(isMissingSourceFreshness);
    return () => {
      setMissingSourceFreshness(false);
    };
  }, [setMissingSourceFreshness, sources]);

  if (!sourceName || !accountId || !sourceCollection) return null;
  if (paginatedSourcesQuery.error) return <>{paginatedSourcesQuery.error.stack}</>;

  return (
    <Page title={sourceName} pageType="sourceCollectionDetails">
      <ResourceDetailsHeader
        headerIcons={headerIcons}
        title={sourceName}
        packageName={undefined}
        files={[]}
        resourceType="source"
        name={sourceName}
        updatedDate={date}
      />
      <div
        className={`${sourceCollection ? 'opacity-100' : 'opacity-0'} duration-300 motion-reduce:duration-0`}
      >
        <DetailsSection heading="Description">
          <ResourceInfoProvider
            resourceType="source"
            resourceIdentifiers={[sourceName]}
            filePath={getPatchPath(firstSource)}
            latestGitSha={sourceCollection?.latestGitSha}
          >
            <DescriptionChangesProvider projectId={projectScope.projectId}>
              <InheritedDescriptionDisplay
                description={description}
                defaultDescription={NO_DESCRIPTION}
                isDescriptionInherited={false}
                inheritedInfo={<span>catalog comment</span>}
                className="prose prose-sm w-full min-w-full dark:prose-invert"
              />
            </DescriptionChangesProvider>
          </ResourceInfoProvider>
        </DetailsSection>
        <DetailsSection heading="Details">
          <ColumnTable
            tableEntries={sourceCollectionDetailTableContents}
            isLoading={sourceCollectionQuery.fetching}
          />
        </DetailsSection>
        <DetailsSection heading="Source tables" withCard={false}>
          <ResourceFilterTable
            pluralizedResultName="sources"
            data={data}
            columns={[
              {
                header: 'Name',
                accessorKey: 'name',
                id: 'name',
                cell: getNameLinkCell(getDetailsPageLink, projectScope),
              },
              {
                header: 'Health',
                accessorKey: 'trustSignals',
                cell: SourceTrustSignalCell,
                size: 90,
                minSize: 90,
                enableSorting: false,
              },
              {
                header: 'Freshness status',
                accessorKey: 'status',
                size: 100,
                cell: NodeStatusCell,
              },
              {
                header: 'Freshness checked at',
                accessorKey: 'snapshottedAt',
                size: 100,
              },
              {
                header: 'Last loaded at',
                accessorKey: 'maxLoadedAt',
                size: 100,
              },
            ]}
            pageInfo={pageInfo}
            isLoading={isInitialLoad}
            isLoadingMore={isLoadingMore}
            pageSize={pageSize}
            fetchMore={paginatedSourcesQuery.fetchMore}
          />
        </DetailsSection>
      </div>
    </Page>
  );
};
